import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { initialStickRows, stickLotLabel, stickRowsToLots } from "./stickLots";

export const STICK_LOT_TITLE = "Which stick did you cut?";

/**
 * Imperative saw stick-lot picker (2026-10-14), shown by Complete on a cut-list Fabrication task
 * (features/queue/taskActions completeTask, through ui.stickLots). Like causePrompt it renders in
 * its own React root inside an ErrorBoundary, on the desktop and the phone alike.
 *
 * `state` = GET /tasks/{id}/stick_lots. Resolves:
 *   {lots: [{mtn_id, sticks}]}  the TRK(s) read off the stick tags (pre-filled when the material
 *                               has exactly one open lot)
 *   {noTag: true}               "No tag on this stick": only offered when some stock is in no lot
 *   null                        Cancel / X / Escape: the task is not completed
 */
export default function stickLotPrompt(state) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;

    function finish(value) {
      if (settled) return;
      settled = true;
      root.unmount();
      container.remove();
      resolve(value);
    }

    root.render(
      <ErrorBoundary
        fallback={(error) => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: "fixed", zIndex: 2001, top: 0, left: 0, right: 0 }}>
            The stick lot picker hit an error: {(error && error.message) || String(error)}. Nothing was saved.
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => finish(null)}>Close</button>
          </div>
        )}
      >
        <StickLotModal state={state} onDone={(v) => finish(v)} onCancel={() => finish(null)} />
      </ErrorBoundary>
    );
  });
}

/** Why no lot is filled in when the lots and the rack disagree (jobq-api fills one in only when
 *  the open lots account exactly for the sticks in stock). */
export function stockGapNote(state) {
  const plural = (n) => `${n} stick${n === 1 ? "" : "s"}`;
  const over = Number(state?.lots_overstated_sticks || 0);
  const untracked = Number(state?.untracked_sticks || 0);
  if (over > 0) {
    return `The lots list ${plural(over)} more than the rack holds (sticks were cut without a lot), so none is filled in: read the tag.`;
  }
  if (untracked > 0) {
    return `${plural(untracked)} in stock ${untracked === 1 ? "has" : "have"} no lot. If this stick's tag has a TRK, pick it.`;
  }
  return "";
}

export function StickLotModal({ state, onDone, onCancel }) {
  const planned = Number(state?.sticks_planned || 0);
  const lots = Array.isArray(state?.candidate_mtns) ? state.candidate_mtns : [];
  const [rows, setRows] = useState(() => initialStickRows(state));
  const [error, setError] = useState("");
  const noTagAllowed = !state?.tracking_required;

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  function save() {
    const out = stickRowsToLots(rows, planned);
    if (!out.ok) { setError(out.error); return; }
    onDone({ lots: out.lots });
  }

  return (
    <Modal show onHide={onCancel} backdrop="static" centered style={{ zIndex: 2000 }}>
      <Modal.Header closeButton>
        <Modal.Title>{STICK_LOT_TITLE}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-2">
          <strong>{state?.material_code || "Stick material"}</strong>
          {planned ? <span className="text-muted"> · {planned} stick{planned === 1 ? "" : "s"} this task</span> : null}
        </div>
        <div className="small text-muted mb-3">
          Read the TRK # on the stick's tag. Its heat goes with the parts and every leftover piece.
        </div>
        {stockGapNote(state) && (
          <div className="alert alert-info py-2 small" data-testid="stick-stock-gap">{stockGapNote(state)}</div>
        )}
        {rows.map((r, i) => (
          <div className="d-flex gap-2 align-items-end mb-2" key={i} data-testid="stick-row">
            <div className="flex-grow-1">
              {i === 0 && <label className="form-label small mb-1">Lot (TRK #)</label>}
              <select
                className="form-select"
                aria-label={`Lot for row ${i + 1}`}
                value={lots.some((l) => String(l.mtn_id) === r.mtn) ? r.mtn : (r.mtn ? "__typed" : "")}
                onChange={(e) => setRow(i, { mtn: e.target.value === "__typed" ? (r.mtn || " ") : e.target.value })}
              >
                <option value="">Pick a lot…</option>
                {lots.map((l) => <option key={l.mtn_id} value={String(l.mtn_id)}>{stickLotLabel(l)}</option>)}
                <option value="__typed">Another TRK # (type it)</option>
              </select>
              {r.mtn && !lots.some((l) => String(l.mtn_id) === r.mtn) && (
                <input
                  className="form-control mt-1"
                  inputMode="numeric"
                  placeholder="TRK # from the tag"
                  aria-label={`Typed TRK for row ${i + 1}`}
                  value={r.mtn.trim()}
                  onChange={(e) => setRow(i, { mtn: e.target.value || " " })}
                />
              )}
            </div>
            <div style={{ width: 90 }}>
              {i === 0 && <label className="form-label small mb-1">Sticks</label>}
              <input
                className="form-control"
                inputMode="numeric"
                aria-label={`Sticks for row ${i + 1}`}
                value={r.sticks}
                onChange={(e) => setRow(i, { sticks: e.target.value.replace(/\D/g, "") })}
              />
            </div>
            {rows.length > 1 && (
              <button type="button" className="btn btn-outline-secondary" aria-label={`Remove row ${i + 1}`}
                      onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>×</button>
            )}
          </div>
        ))}
        {rows.length < 4 && (
          <button type="button" className="btn btn-link btn-sm px-0"
                  onClick={() => setRows((rs) => [...rs, { mtn: "", sticks: "1" }])}>
            + Sticks from another lot
          </button>
        )}
        {error && <div className="alert alert-warning py-2 mt-2 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        {noTagAllowed ? (
          <Button variant="outline-secondary" onClick={() => onDone({ noTag: true })}>No tag on this stick</Button>
        ) : (
          <span className="small text-muted">Every stick in stock has a lot: pick its TRK.</span>
        )}
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={save}>Save &amp; complete</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
